import { useMemo } from 'react'

import { type ConfigurationHistoryEvent } from '@/model/firestore/useConfigurationHistory'

export type ConfigurationType =
  'assignment' | 'mobileRequests' | 'waitTime' | 'assistedAssignment' | 'combinedAssignment'

// Combined assignment value: 'disabled' when assisted assignment is off, otherwise the assignment mode
export type CombinedAssignmentValue = 'disabled' | 'manual' | 'trf' | 'solver' | 'legacy'

export interface TimeSegment {
  start: Date
  end: Date
  value: string | boolean
  durationMs: number
  source?: 'central' | 'evening' // For mobileRequests type only
}

export interface TypeStats {
  type: ConfigurationType
  segments: TimeSegment[]
  switchCount: number
  valueBreakdown: Record<string, { durationMs: number; percentage: number }>
}

export interface ConfigurationHistoryStats {
  assignment: TypeStats | null
  mobileRequests: TypeStats | null
  waitTime: TypeStats | null
  assistedAssignment: TypeStats | null
  combinedAssignment: TypeStats | null
  totalSwitches: number
}

function calculateValueBreakdown(segments: TimeSegment[]): Record<string, { durationMs: number; percentage: number }> {
  const valueBreakdown: Record<string, { durationMs: number; percentage: number }> = {}
  const totalDuration = segments.reduce((sum, seg) => sum + Math.max(0, seg.durationMs), 0)

  for (const segment of segments) {
    const key = String(segment.value)
    if (!valueBreakdown[key]) {
      valueBreakdown[key] = { durationMs: 0, percentage: 0 }
    }
    valueBreakdown[key].durationMs += Math.max(0, segment.durationMs)
  }

  for (const key of Object.keys(valueBreakdown)) {
    valueBreakdown[key].percentage = totalDuration > 0 ? (valueBreakdown[key].durationMs / totalDuration) * 100 : 0
  }

  return valueBreakdown
}

function calculateTypeStats(
  events: ConfigurationHistoryEvent[],
  type: ConfigurationType,
  eveningStartTime?: Date,
  eveningEndTime?: Date
): TypeStats | null {
  const typeEvents = events.filter((e) => e.type === type)

  if (typeEvents.length === 0 || !eveningStartTime) {
    return null
  }

  const now = new Date()

  // If we're before the evening start time, there's no operating time yet
  if (now < eveningStartTime) {
    // Find initial value from events before start
    const eventsBeforeStart = typeEvents.filter((e) => e.timestamp < eveningStartTime)
    const sortedBeforeStart = [...eventsBeforeStart].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
    const lastEventBeforeStart = sortedBeforeStart.length > 0 ? sortedBeforeStart.at(-1) : null
    const initialValue = lastEventBeforeStart?.newValue ?? null

    // Return stats with no segments but show what the initial value will be
    return {
      type,
      segments: [],
      switchCount: 0,
      valueBreakdown: initialValue !== null ? { [String(initialValue)]: { durationMs: 0, percentage: 100 } } : {},
    }
  }

  const endTime = eveningEndTime && eveningEndTime < now ? eveningEndTime : now

  // Filter events to only those within operating hours
  const eventsInRange = typeEvents.filter((e) => e.timestamp >= eveningStartTime && e.timestamp <= endTime)

  // Find the last event before start time to determine initial value at evening start
  const eventsBeforeStart = typeEvents.filter((e) => e.timestamp < eveningStartTime)
  const lastEventBeforeStart = eventsBeforeStart.length > 0 ? eventsBeforeStart.at(-1) : null

  // Determine initial value at evening start
  const initialValue = lastEventBeforeStart?.newValue ?? eventsInRange[0]?.previousValue ?? null

  const segments: TimeSegment[] = []

  // Helper to get source from mobileRequests event
  const getSource = (event: ConfigurationHistoryEvent | null | undefined): 'central' | 'evening' | undefined => {
    if (event?.type === 'mobileRequests') {
      return event.source
    }
    return undefined
  }

  if (eventsInRange.length === 0) {
    // No events in range - show single segment with initial value (if known)
    if (initialValue !== null) {
      segments.push({
        start: eveningStartTime,
        end: endTime,
        value: initialValue,
        durationMs: endTime.getTime() - eveningStartTime.getTime(),
        source: getSource(lastEventBeforeStart),
      })
    }
    return {
      type,
      segments,
      switchCount: 0,
      valueBreakdown: calculateValueBreakdown(segments),
    }
  }

  // First segment: from evening start to first event in range
  const firstEventInRange = eventsInRange[0]
  // Use initialValue if we have pre-start events, otherwise use first event's previousValue
  const startValue = initialValue ?? firstEventInRange.previousValue
  segments.push({
    start: eveningStartTime,
    end: firstEventInRange.timestamp,
    value: startValue,
    durationMs: firstEventInRange.timestamp.getTime() - eveningStartTime.getTime(),
    source: getSource(lastEventBeforeStart),
  })

  // Segments for events in range
  for (let i = 0; i < eventsInRange.length; i++) {
    const event = eventsInRange[i]
    const nextEvent = eventsInRange[i + 1]
    const segmentEnd = nextEvent ? nextEvent.timestamp : endTime

    segments.push({
      start: event.timestamp,
      end: segmentEnd,
      value: event.newValue,
      durationMs: segmentEnd.getTime() - event.timestamp.getTime(),
      source: getSource(event),
    })
  }

  // Only count actual changes (exclude initial snapshots where previousValue === newValue)
  const actualChanges = eventsInRange.filter((e) => e.previousValue !== e.newValue)

  return {
    type,
    segments,
    switchCount: actualChanges.length,
    valueBreakdown: calculateValueBreakdown(segments),
  }
}

/**
 * Calculates combined assignment stats by merging assistedAssignment and assignment events.
 * When assisted assignment is disabled, the value is 'disabled'.
 * When enabled, the value is the current assignment mode.
 */
function calculateCombinedAssignmentStats(
  events: ConfigurationHistoryEvent[],
  eveningStartTime?: Date,
  eveningEndTime?: Date
): TypeStats | null {
  const assistedEvents = events.filter((e) => e.type === 'assistedAssignment')
  const assignmentEvents = events.filter((e) => e.type === 'assignment')

  if ((assistedEvents.length === 0 && assignmentEvents.length === 0) || !eveningStartTime) {
    return null
  }

  const now = new Date()
  const endTime = eveningEndTime && eveningEndTime < now ? eveningEndTime : now

  // If we're before the evening start time, return early
  if (now < eveningStartTime) {
    return {
      type: 'combinedAssignment',
      segments: [],
      switchCount: 0,
      valueBreakdown: {},
    }
  }

  // Merge and sort all relevant events by timestamp
  interface MergedEvent {
    timestamp: Date
    type: 'assisted' | 'assignment'
    newValue: boolean | string
  }
  const mergedEvents: MergedEvent[] = [
    ...assistedEvents.map((e) => ({ timestamp: e.timestamp, type: 'assisted' as const, newValue: e.newValue })),
    ...assignmentEvents.map((e) => ({ timestamp: e.timestamp, type: 'assignment' as const, newValue: e.newValue })),
  ].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())

  // Determine initial state from events before evening start
  const eventsBeforeStart = mergedEvents.filter((e) => e.timestamp < eveningStartTime)
  let isAssistedEnabled = false
  let currentAssignmentMode = 'manual'

  for (const event of eventsBeforeStart) {
    if (event.type === 'assisted') {
      isAssistedEnabled = event.newValue === true
    } else {
      currentAssignmentMode = event.newValue as string
    }
  }

  // Filter events within the operating range
  const eventsInRange = mergedEvents.filter((e) => e.timestamp >= eveningStartTime && e.timestamp <= endTime)

  // Build segments
  const segments: TimeSegment[] = []
  let segmentStart = eveningStartTime
  let currentValue: CombinedAssignmentValue = isAssistedEnabled
    ? (currentAssignmentMode as CombinedAssignmentValue)
    : 'disabled'
  let switchCount = 0

  for (const event of eventsInRange) {
    // Create segment for the period before this event
    if (event.timestamp > segmentStart) {
      segments.push({
        start: segmentStart,
        end: event.timestamp,
        value: currentValue,
        durationMs: event.timestamp.getTime() - segmentStart.getTime(),
      })
    }

    // Update state based on event
    const previousValue = currentValue
    if (event.type === 'assisted') {
      isAssistedEnabled = event.newValue === true
    } else {
      currentAssignmentMode = event.newValue as string
    }
    currentValue = isAssistedEnabled ? (currentAssignmentMode as CombinedAssignmentValue) : 'disabled'

    // Count actual changes (not initial snapshots)
    if (previousValue !== currentValue) {
      switchCount++
    }

    segmentStart = event.timestamp
  }

  // Final segment to end time
  if (segmentStart < endTime) {
    segments.push({
      start: segmentStart,
      end: endTime,
      value: currentValue,
      durationMs: endTime.getTime() - segmentStart.getTime(),
    })
  }

  return {
    type: 'combinedAssignment',
    segments,
    switchCount,
    valueBreakdown: calculateValueBreakdown(segments),
  }
}

export function useConfigurationHistoryStats(
  events: ConfigurationHistoryEvent[],
  eveningStartTime?: Date,
  eveningEndTime?: Date
): ConfigurationHistoryStats {
  return useMemo(() => {
    const sortedEvents = [...events].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())

    const assignment = calculateTypeStats(sortedEvents, 'assignment', eveningStartTime, eveningEndTime)
    const mobileRequests = calculateTypeStats(sortedEvents, 'mobileRequests', eveningStartTime, eveningEndTime)
    const waitTime = calculateTypeStats(sortedEvents, 'waitTime', eveningStartTime, eveningEndTime)
    const assistedAssignment = calculateTypeStats(sortedEvents, 'assistedAssignment', eveningStartTime, eveningEndTime)
    const combinedAssignment = calculateCombinedAssignmentStats(sortedEvents, eveningStartTime, eveningEndTime)

    // Use combinedAssignment switch count instead of separate assisted + assignment counts
    const totalSwitches =
      (combinedAssignment?.switchCount ?? 0) + (mobileRequests?.switchCount ?? 0) + (waitTime?.switchCount ?? 0)

    return {
      assignment,
      mobileRequests,
      waitTime,
      assistedAssignment,
      combinedAssignment,
      totalSwitches,
    }
  }, [events, eveningStartTime, eveningEndTime])
}

// Helper to format duration in human-readable format
export function formatDuration(ms: number): string {
  const minutes = Math.floor(ms / 60_000)
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60

  if (hours > 0) {
    return `${hours}h ${remainingMinutes}m`
  }
  return `${minutes}m`
}
